'use client';

/**
 * App-root error.tsx — branded error boundary for the root layout.
 *
 * Covers routes outside /dashboard (marketing pages, auth, etc.).
 * Renders VerbalLogo + clear CTA. Light + dark via CSS tokens.
 * Does NOT depend on org/user data — safe in error contexts.
 */

import { useEffect } from 'react';
import Link from 'next/link';
import { VerbalLogo } from '@/components/verbal-logo';
import { captureException } from '@/lib/analytics/posthog';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function RootError({ error, reset }: ErrorPageProps) {
  useEffect(() => {
    console.error('Root error:', error);
    captureException(error, { digest: error.digest });
  }, [error]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-[--color-paper] px-6 py-16">
      <div className="w-full max-w-sm">
        {/* Wordmark */}
        <div className="flex justify-center mb-10">
          <VerbalLogo className="h-6" aria-label="Verbal" />
        </div>

        {/* Message */}
        <div className="text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[--color-graphite] mb-3">
            Error
          </p>
          <h1 className="font-serif-display text-3xl text-[--color-ink] text-balance leading-tight">
            Something went wrong
          </h1>
          <p className="mt-3 text-sm text-[--color-graphite] text-pretty">
            An unexpected error occurred. Please try again or contact support
            if the problem persists.
          </p>
          {error.digest && (
            <p className="mt-2 font-mono text-[11px] text-[--color-graphite]/70 tabular-nums">
              Error ID: {error.digest}
            </p>
          )}
        </div>

        {/* CTAs */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center px-5 py-2.5 bg-[--color-ink] text-[--color-paper] font-mono text-[11px] uppercase tracking-[0.12em] rounded-sm transition-colors hover:bg-[--color-ink]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-ink]/40"
          >
            Try again
          </button>
          <Link
            href="/"
            className="inline-flex items-center justify-center px-5 py-2.5 border border-[--color-rule] text-[--color-graphite] font-mono text-[11px] uppercase tracking-[0.12em] rounded-sm transition-colors hover:border-[--color-ink] hover:text-[--color-ink] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-ink]/40"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}
